/**
 * iCloud (Calendrier et Mail) — names, action slugs and identifier formats shared by the Worker (tools, approvals,
 * risk) and the web client. Both services use ONE app-specific password; each is a "native app" toolkit
 * (`icloudmail`, `icloudcalendar`) with its own switch, status and monitoring consent, never a Composio toolkit.
 *
 * Identifiers given to the model (never URLs):
 * - message: `<encodeURIComponent(folder)>:<UIDVALIDITY>:<UID>` (`INBOX:1730000000:4521`), parsed with the last two
 *   colons; a different current UIDVALIDITY means the message must be searched again;
 * - event: `<calendar id>/<resource name>.ics`, plus `#<RECURRENCE-ID>` for one instance of a series
 *   (`home/3F2A51B0-77A1-4C1E.ics`, `work/B12C.ics#20301111T140000Z`), always resolved under the stored calendars.
 */
import type { Risk } from "./contracts";

export type IcloudService = "mail" | "calendar";
export type IcloudToolkit = "icloudmail" | "icloudcalendar";

export const ICLOUD_SERVICES: Record<IcloudService, { toolkit: IcloudToolkit; name: string }> = {
  mail: { toolkit: "icloudmail", name: "iCloud Mail" },
  calendar: { toolkit: "icloudcalendar", name: "Calendrier iCloud" },
};

export const ICLOUD_TOOLKITS: readonly IcloudToolkit[] = ["icloudmail", "icloudcalendar"];

export function isIcloudToolkit(slug: string): slug is IcloudToolkit {
  return (ICLOUD_TOOLKITS as readonly string[]).includes(slug);
}

export function serviceOfToolkit(toolkit: IcloudToolkit): IcloudService {
  return toolkit === "icloudmail" ? "mail" : "calendar";
}

/**
 * Every iCloud action, its risk (shared/risk.ts icloudRisk) and the approval kind it maps onto (the owner's rule:
 * act by default, ask only for high-stakes actions). The owner's own calendar and mailbox are changed freely (they ask
 * only in the "prudent" autonomy); changing or deleting an event that has attendees other than the user reaches them
 * ("calendar_invite", external); sending and replying ("send_email", "reply_email") and moving mail to the trash
 * ("delete") always ask. Unknown iCloud slugs are "critical".
 */
export const ICLOUD_ACTIONS = {
  ICLOUDCALENDAR_LIST_EVENTS: { service: "calendar", risk: "read", kind: null },
  ICLOUDCALENDAR_CREATE_EVENT: { service: "calendar", risk: "write", kind: null },
  ICLOUDCALENDAR_UPDATE_EVENT: { service: "calendar", risk: "write", kind: null },
  ICLOUDCALENDAR_DELETE_EVENT: { service: "calendar", risk: "write", kind: null },
  ICLOUDMAIL_SEARCH_EMAILS: { service: "mail", risk: "read", kind: null },
  ICLOUDMAIL_READ_EMAIL: { service: "mail", risk: "read", kind: null },
  ICLOUDMAIL_MARK_READ: { service: "mail", risk: "write", kind: null },
  ICLOUDMAIL_MARK_UNREAD: { service: "mail", risk: "write", kind: null },
  ICLOUDMAIL_FLAG: { service: "mail", risk: "write", kind: null },
  ICLOUDMAIL_UNFLAG: { service: "mail", risk: "write", kind: null },
  ICLOUDMAIL_ARCHIVE_EMAIL: { service: "mail", risk: "write", kind: null },
  ICLOUDMAIL_MOVE_EMAIL: { service: "mail", risk: "write", kind: null },
  ICLOUDMAIL_SAVE_DRAFT: { service: "mail", risk: "write", kind: null },
  ICLOUDMAIL_TRASH_EMAIL: { service: "mail", risk: "critical", kind: "delete" },
  ICLOUDMAIL_SEND_EMAIL: { service: "mail", risk: "external", kind: "send_email" },
  ICLOUDMAIL_REPLY_EMAIL: { service: "mail", risk: "external", kind: "reply_email" },
} as const satisfies Record<string, { service: IcloudService; risk: Exclude<Risk, "forbidden">; kind: IcloudApprovalKind | null }>;

/** The approval kinds (friction policy) iCloud actions map onto; calendar_invite is decided per event (attendees). */
export type IcloudApprovalKind = "send_email" | "reply_email" | "calendar_invite" | "delete";

export type IcloudSlug = keyof typeof ICLOUD_ACTIONS;

/** Any slug of the iCloud family (known or not): never sent to Composio. */
export function isIcloudSlug(slug: string): boolean {
  return /^ICLOUD(?:MAIL|CALENDAR)_/.test(slug.trim().toUpperCase());
}

export function knownIcloudSlug(slug: string): IcloudSlug | null {
  const s = slug.trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(ICLOUD_ACTIONS, s) ? (s as IcloudSlug) : null;
}

/** One e-mail address, strictly (shared by the tool schemas, the approval executor and the message builder). */
const EMAIL_ADDRESS =
  /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]{1,64}@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

export function isEmailAddress(value: string): boolean {
  return value.length <= 254 && EMAIL_ADDRESS.test(value) && !value.includes("..");
}

/** "Marc <marc@example.com>", "marc@example.com" → {name, address (lower case)}; null when not a strict address. */
export function parseMailbox(input: string): { name: string | null; address: string } | null {
  const s = input.replace(/[\r\n\t]+/g, " ").trim();
  const angle = /^(.*?)\s*<([^<>\s]+)>$/.exec(s);
  const address = (angle ? angle[2]! : s).trim().toLowerCase();
  if (!isEmailAddress(address)) return null;
  const name = angle ? angle[1]!.trim().replace(/^"(.*)"$/s, "$1").trim() : "";
  return { name: name ? name.slice(0, 120) : null, address };
}

/**
 * Event text the model or an edited approval card may give: titles and places are one line (no control character at
 * all), notes may hold line breaks (LF) and tabs only. A raw CR or LF would otherwise end an iCalendar content line.
 */
export const ICLOUD_LINE_CONTROL = /[\u0000-\u001f\u007f\u2028\u2029]/;
export const ICLOUD_NOTES_CONTROL = /[\u0000-\u0008\u000b-\u001f\u007f\u2028\u2029]/;
/** At most this many people invited in one change. */
export const ICLOUD_INVITE_MAX = 20;

/** Event ids: `<calendar id>/<resource>.ics[#<RECURRENCE-ID>]`. */
export const ICLOUD_EVENT_ID = /^([A-Za-z0-9._-]{1,80})\/([A-Za-z0-9._@+=-]{1,200}\.ics)(?:#(\d{8}(?:T\d{6}Z)?))?$/;
/** Message ids: `<encoded folder>:<UIDVALIDITY>:<UID>`. */
export const ICLOUD_MESSAGE_ID = /^([^:\s]{1,300}):(\d{1,10}):(\d{1,10})$/;
/** Longest event id offered for writes (reads show `id: null` beyond it). */
export const ICLOUD_EVENT_ID_MAX = 180;
